<template>
  <el-button
    v-bind="$attrs"
    :type="type"
    :size="size"
    :plain="plain"
    :text="textButton"
    :loading="buttonLoading"
    :disabled="buttonDisabled"
    :native-type="nativeType"
    @click.stop="handleClick"
  >
    <el-icon v-if="icon" class="icon">
      <component :is="icon" />
    </el-icon>
    <span>
      <slot>{{ buttonText }}</slot>
    </span>
  </el-button>
</template>

<script setup lang="ts">
import { computed, ref, type PropType } from 'vue'
import { useI18n } from 'vue-i18n'

type SubmitAction = (event: Event) => Promise<unknown> | unknown

const { t } = useI18n()

const props = defineProps({
  icon: {
    type: [String, Object],
    default: null
  },
  text: {
    type: String,
    default: ''
  },
  loadingText: {
    type: String,
    default: ''
  },
  isI18n: {
    type: Boolean,
    default: true
  },
  loadingTextIsI18n: {
    type: Boolean,
    default: true
  },
  type: {
    type: String,
    default: 'primary'
  },
  size: {
    type: String,
    default: 'default'
  },
  plain: {
    type: Boolean,
    default: false
  },
  textButton: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  nativeType: {
    type: String,
    default: 'button'
  },
  autoDisableWhenLoading: {
    type: Boolean,
    default: true
  },
  action: {
    type: Function as PropType<SubmitAction>,
    default: null
  }
})

const emit = defineEmits(['click'])
const internalLoading = ref(false)

const translateText = (text: string, useI18nText: boolean) => {
  return useI18nText ? t(text) : text
}

const buttonLoading = computed(() => {
  return props.loading || internalLoading.value
})

const buttonDisabled = computed(() => {
  return props.disabled || (props.autoDisableWhenLoading && buttonLoading.value)
})

const buttonText = computed(() => {
  if (buttonLoading.value && props.loadingText) {
    return translateText(props.loadingText, props.loadingTextIsI18n)
  }

  return translateText(props.text, props.isI18n)
})

const handleClick = async (event: Event) => {
  event.preventDefault()

  if (buttonDisabled.value) {
    return
  }

  if (!props.action) {
    emit('click', event)
    return
  }

  internalLoading.value = true

  try {
    await props.action(event)
  } finally {
    internalLoading.value = false
  }
}
</script>

<style scoped>
.icon {
  margin-right: 4px;
}
</style>
